import * as React from 'react';
import { cn } from '../../utils/cn';

type ListElement = 'ul' | 'ol';
type ListStyleType = 'disc' | 'circle' | 'square' | 'decimal' | 'alpha' | 'roman' | 'none';
type ListSpacing = 'tight' | 'normal' | 'loose';

export interface ListProps {
  as?: ListElement;
  children: React.ReactNode;
  styleType?: ListStyleType;
  spacing?: ListSpacing;
  columns?: 1 | 2 | 3 | 4;
  horizontal?: boolean;
  className?: string;
}

export interface ListItemProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}

const styleTypeClasses: Record<ListStyleType, string> = {
  disc: 'list-disc',
  circle: 'list-circle',
  square: 'list-square',
  decimal: 'list-decimal',
  alpha: 'list-alpha',
  roman: 'list-roman',
  none: 'list-none',
};

const spacingClasses: Record<ListSpacing, string> = {
  tight: 'space-y-1',
  normal: 'space-y-2',
  loose: 'space-y-4',
};

const columnsClasses: Record<number, string> = {
  1: '',
  2: 'columns-2',
  3: 'columns-3',
  4: 'columns-4',
};

export const List = React.forwardRef<HTMLUListElement | HTMLOListElement, ListProps>(
  (
    {
      as: Component = 'ul',
      children,
      styleType = Component === 'ul' ? 'disc' : 'decimal',
      spacing = 'normal',
      columns = 1,
      horizontal = false,
      className,
    },
    ref
  ) => {
    return (
      <Component
        ref={ref as any}
        className={cn(
          styleTypeClasses[styleType],
          !horizontal && spacingClasses[spacing],
          horizontal && 'flex flex-wrap gap-4',
          columnsClasses[columns],
          styleType !== 'none' && !horizontal && 'pl-5',
          'text-base-content',
          className
        )}
      >
        {children}
      </Component>
    );
  }
);

List.displayName = 'List';

export const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(
  ({ children, icon, className }, ref) => {
    if (icon) {
      return (
        <li ref={ref} className={cn('flex items-start gap-2', className)}>
          <span className="flex-shrink-0 mt-0.5" aria-hidden="true">
            {icon}
          </span>
          <span>{children}</span>
        </li>
      );
    }

    return (
      <li ref={ref} className={className}>
        {children}
      </li>
    );
  }
);

ListItem.displayName = 'ListItem';
